"use client";

import { useId, useRef } from "react";
import { parseUserNumber } from "@/ui/components/ui/numeric-input";

/** Tolerance for comparing preset values to user-entered floats (e.g. parseFloat drift). */
const PRESET_EPS = 1e-5;

export function approxEqualPreset(a: number, b: number): boolean {
	return Math.abs(a - b) < PRESET_EPS;
}

export function isPresetValue(values: number[], selected: number): boolean {
	return values.some((v) => approxEqualPreset(v, selected));
}

interface PresetChipsProps {
	values: number[];
	selected: number;
	onChange: (value: number) => void;
	unit: string;
	accessibleLabel: string;
	min?: number;
	/** Values that are stockable in the shop — render a small success dot on those chips. */
	shopValues?: number[];
}

export function PresetChips({
	values,
	selected,
	onChange,
	unit,
	accessibleLabel,
	min = 0,
	shopValues,
}: PresetChipsProps) {
	const isCustom = !isPresetValue(values, selected);
	const inputId = useId();
	const inputRef = useRef<HTMLInputElement>(null);

	const chipBase =
		"inline-flex min-h-11 min-w-[54px] items-center justify-center gap-1.5 rounded-full border-[1.5px] px-3.5 py-2 font-mono text-sm font-semibold tabular-nums transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98]";
	const chipActive = "border-primary bg-primary text-primary-foreground shadow-md";
	const chipInactive = "border-border bg-card text-foreground hover:border-primary/60";

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{values.map((value) => {
				const isThisPreset = approxEqualPreset(selected, value) && !isCustom;
				const inShop = shopValues?.some((v) => approxEqualPreset(v, value)) ?? false;
				return (
					<button
						key={value}
						type="button"
						aria-pressed={isThisPreset}
						onClick={() => {
							onChange(value);
							if (inputRef.current) inputRef.current.value = "";
						}}
						className={`${chipBase} ${isThisPreset ? chipActive : chipInactive}`}
					>
						{inShop && (
							<span
								aria-hidden
								className={`h-1.5 w-1.5 shrink-0 rounded-full ${isThisPreset ? "bg-success-foreground/70" : "bg-success"}`}
							/>
						)}
						<span>
							{value} <span className="font-sans text-xs font-medium opacity-70">{unit}</span>
						</span>
					</button>
				);
			})}

			{/* Always-visible compact custom input. Dashed border (prototype "···"/custom
			    affordance) that goes solid navy once a custom value is active. */}
			<label
				htmlFor={inputId}
				className={`flex min-h-11 cursor-text items-center gap-0.5 rounded-lg border-[1.5px] px-2 py-1 transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${
					isCustom ? "bg-primary/10 border-solid border-primary" : "border-dashed border-border bg-card"
				}`}
			>
				<input
					id={inputId}
					ref={inputRef}
					aria-label={accessibleLabel}
					type="text"
					inputMode="decimal"
					pattern="[0-9.,]*"
					defaultValue={isCustom ? selected : ""}
					placeholder="..."
					onFocus={(e) => {
						if (typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches) {
							e.target.select();
						}
					}}
					onChange={(e) => {
						const parsed = parseUserNumber(e.target.value);
						if (parsed !== null && parsed >= min) onChange(parsed);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter") e.currentTarget.blur();
					}}
					className={`w-12 bg-transparent text-center text-sm font-medium outline-none placeholder:text-muted-foreground ${
						isCustom ? "text-primary" : "text-foreground"
					}`}
				/>
				<span aria-hidden="true" className="text-xs text-muted-foreground">
					{unit}
				</span>
			</label>
		</div>
	);
}
